import { ProjectActivity } from "../types/ActivityTypes";

/**
 * Orden canónico de las actividades de un reporte diario.
 *
 * El orden lo define la matriz de actividades del proyecto (`projects_activities.order`),
 * pero los detalles de un reporte diario llegan del backend sin ese campo y sin ORDER BY.
 * Estos helpers reconstruyen el orden a partir de las actividades del proyecto.
 *
 * Se usa tanto al abrir un reporte para editar como al "Copiar día anterior", para que
 * las dos rutas muestren las actividades en la misma secuencia.
 */

/** Actividad sin orden conocido: se manda al final, conservando su posición relativa. */
const UNKNOWN_ORDER = 9999;

/** Normaliza texto para comparar nombres de actividad sin tildes ni mayúsculas. */
export const normalizeText = (text: string | undefined | null): string => {
  if (!text) return "";
  return text.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim();
};

/**
 * Construye el mapa de orden a partir de las actividades del proyecto.
 *
 * Se indexa por id y también por nombre normalizado: hay reportes antiguos cuyos detalles
 * no traen el id de la actividad maestra, y en esos casos el nombre es el único enganche.
 */
export const buildActivityOrderMap = (projectActivities: unknown): Map<string, number> => {
  const orderMap = new Map<string, number>();
  if (!Array.isArray(projectActivities)) return orderMap;

  projectActivities.forEach((pa: ProjectActivity) => {
    if (pa?.activity?.id) orderMap.set(`ID:${pa.activity.id}`, pa.order ?? UNKNOWN_ORDER);
    if (pa?.activity?.name) orderMap.set(`NAME:${normalizeText(pa.activity.name)}`, pa.order ?? UNKNOWN_ORDER);
  });
  return orderMap;
};

/** Orden de una actividad de reporte: primero por id de la actividad maestra, luego por nombre. */
export const getActivityOrder = (item: any, orderMap: Map<string, number>): number => {
  const masterId = item?.activity?.id;
  if (masterId) {
    const idKey = `ID:${masterId}`;
    if (orderMap.has(idKey)) return orderMap.get(idKey)!;
  }

  const name = item?.activityName || item?.activity?.name;
  if (name) {
    const nameKey = `NAME:${normalizeText(name)}`;
    if (orderMap.has(nameKey)) return orderMap.get(nameKey)!;
  }

  return UNKNOWN_ORDER;
};

/**
 * Devuelve una copia de las actividades ordenada según la matriz del proyecto.
 *
 * No muta el arreglo recibido. Si el mapa viene vacío (por ejemplo, si falló la carga de
 * las actividades del proyecto) se conserva el orden original en lugar de reordenar a ciegas.
 */
export const sortActivitiesByProjectOrder = <T,>(activities: T[], orderMap: Map<string, number>): T[] => {
  if (orderMap.size === 0) return [...activities];
  return [...activities].sort((a, b) => getActivityOrder(a, orderMap) - getActivityOrder(b, orderMap));
};
